import {
  ApiError,
  ApiNetworkError,
  type ExecutionDetail,
  type ExecutionListItem,
  type ExecutionListPage,
  type ExecutionPeriod,
  type ExecutionStatusFilter,
  adminAuthLogout,
  adminExecutionDetail,
  adminExecutionsCsvUrl,
  adminExecutionsList,
} from '@cortex/web-api/clients/admin/api';
import { Wordmark } from '@cortex/web-api/ui/admin/Wordmark';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  onSessionExpired: () => void;
  onLoggedOut: () => void;
  onSwitchToClients: () => void;
  onSwitchToFleet: () => void;
}

export function ExecutionsScreen({
  onSessionExpired,
  onLoggedOut,
  onSwitchToClients,
  onSwitchToFleet,
}: Props) {
  const [period, setPeriod] = useState<ExecutionPeriod>('7d');
  const [status, setStatus] = useState<ExecutionStatusFilter>('failed');
  const [tenant, setTenant] = useState('');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState<25 | 50>(25);
  const [cursor, setCursor] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [data, setData] = useState<ExecutionListPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ExecutionListItem | null>(null);
  const [detail, setDetail] = useState<ExecutionDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const detailRequestRef = useRef(0);
  const detailAbortRef = useRef<AbortController | null>(null);

  const params = useMemo(
    () => ({
      period,
      status,
      tenant: tenant.trim() || undefined,
      q: q.trim() || undefined,
      limit,
      cursor,
    }),
    [period, status, tenant, q, limit, cursor],
  );
  const { cursor: _cursor, ...csvParams } = params;
  const csvUrl = adminExecutionsCsvUrl(csvParams);

  async function load(refresh = false) {
    setLoading(true);
    setError(null);
    try {
      const res = await adminExecutionsList({ ...params, refresh });
      setData(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      setError(
        err instanceof ApiNetworkError
          ? "Couldn't reach the server."
          : 'Could not load executions.',
      );
    } finally {
      setLoading(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: params memo is the intended subscription.
  useEffect(() => {
    void load(false);
  }, [params]);

  useEffect(() => {
    return () => {
      detailAbortRef.current?.abort();
    };
  }, []);

  async function openDetail(item: ExecutionListItem) {
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    detailAbortRef.current?.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;
    setSelected(item);
    setDetail(null);
    setDetailError(null);
    try {
      const nextDetail = await adminExecutionDetail(item, controller.signal);
      if (detailRequestRef.current !== requestId || controller.signal.aborted) return;
      setDetail(nextDetail);
    } catch (err) {
      if (controller.signal.aborted || detailRequestRef.current !== requestId) return;
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      setDetailError('Could not load detail.');
    }
  }

  async function handleSignOut() {
    await adminAuthLogout();
    onLoggedOut();
  }

  function closeDetail() {
    detailRequestRef.current += 1;
    detailAbortRef.current?.abort();
    detailAbortRef.current = null;
    setSelected(null);
    setDetail(null);
    setDetailError(null);
  }

  function resetPage() {
    setCursor(null);
    setHistory([]);
  }

  return (
    <div className="app">
      <header className="app-header" style={{ justifyContent: 'space-between', display: 'flex' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <img src="/acel-cortex-mark.svg" alt="" width={32} height={32} />
          <Wordmark />
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onSwitchToClients}
            className="btn-secondary"
            style={navButton}
          >
            clients
          </button>
          <button
            type="button"
            onClick={onSwitchToFleet}
            className="btn-secondary"
            style={navButton}
          >
            fleet
          </button>
          <button type="button" onClick={handleSignOut} className="btn-secondary" style={navButton}>
            sign out
          </button>
        </div>
      </header>
      <main
        className="app-main"
        style={{ overflow: 'auto', padding: 'var(--space-8) var(--space-6)' }}
      >
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gap: 'var(--space-6)' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 'var(--space-4)',
              flexWrap: 'wrap',
            }}
          >
            <h1 className="h2" style={{ color: 'var(--ink)' }}>
              executions
            </h1>
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => void load(true)}
                disabled={loading}
                className="btn-secondary"
              >
                {loading ? 'refreshing...' : 'refresh'}
              </button>
              <a href={csvUrl} className="btn-secondary" style={{ textDecoration: 'none' }}>
                csv
              </a>
            </div>
          </div>

          <div className="card" style={{ padding: 'var(--space-5)' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 'var(--space-4)',
              }}
            >
              <SegmentedControl
                label="status"
                value={status}
                onChange={(value) => {
                  setStatus(value as ExecutionStatusFilter);
                  resetPage();
                }}
                options={[
                  ['all', 'All'],
                  ['failed', 'Failed'],
                  ['uncertain', 'Uncertain'],
                ]}
              />
              <SegmentedControl
                label="period"
                value={period}
                onChange={(value) => {
                  setPeriod(value as ExecutionPeriod);
                  resetPage();
                }}
                options={[
                  ['24h', '24h'],
                  ['7d', '7d'],
                  ['30d', '30d'],
                ]}
              />
              <LabeledInput
                label="tenant"
                value={tenant}
                onChange={(value) => {
                  setTenant(value);
                  resetPage();
                }}
              />
              <LabeledInput
                label="search"
                value={q}
                onChange={(value) => {
                  setQ(value);
                  resetPage();
                }}
              />
              <LabeledSelect
                label="rows"
                value={String(limit)}
                onChange={(value) => {
                  setLimit(value === '50' ? 50 : 25);
                  resetPage();
                }}
                options={[
                  ['25', '25'],
                  ['50', '50'],
                ]}
              />
            </div>
          </div>

          <Warnings data={data} />

          {error ? (
            <div className="card" style={{ padding: 'var(--space-6)' }}>
              <p className="field-error">{error}</p>
            </div>
          ) : (
            <ExecutionTable
              data={data}
              loading={loading}
              status={status}
              onSelect={(item) => void openDetail(item)}
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
            <button
              type="button"
              className="btn-secondary"
              disabled={history.length === 0 || loading}
              onClick={() => {
                const nextHistory = history.slice(0, -1);
                setCursor(history.at(-1) ?? null);
                setHistory(nextHistory);
              }}
            >
              previous
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={!data?.nextCursor || loading}
              onClick={() => {
                setHistory((h) => [...h, cursor ?? '']);
                setCursor(data?.nextCursor ?? null);
              }}
            >
              next
            </button>
          </div>
        </div>
      </main>
      <footer className="app-footer">acel.cortex internal</footer>
      {selected && (
        <DetailDrawer item={selected} detail={detail} error={detailError} onClose={closeDetail} />
      )}
    </div>
  );
}

const navButton = { padding: '8px 16px', fontSize: 'var(--fs-small)' };

function LabeledSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label className="stack-tight">
      <span className="field-label">{label}</span>
      <select className="field-input" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([optionValue, text]) => (
          <option key={optionValue} value={optionValue}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

function SegmentedControl({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <fieldset className="stack-tight" style={{ border: 0, minInlineSize: 0 }}>
      <legend className="field-label">{label}</legend>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
          border: '1px solid var(--rule)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          minHeight: 42,
        }}
      >
        {options.map(([optionValue, text]) => {
          const selected = value === optionValue;
          return (
            <button
              key={optionValue}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(optionValue)}
              style={{
                border: 0,
                borderRight: '1px solid var(--rule)',
                background: selected ? 'var(--ink)' : 'var(--paper)',
                color: selected ? 'var(--paper)' : 'var(--ink)',
                fontSize: 'var(--fs-small)',
                padding: '8px 10px',
                minWidth: 0,
              }}
            >
              {text}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="stack-tight">
      <span className="field-label">{label}</span>
      <input className="field-input" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function Warnings({ data }: { data: ExecutionListPage | null }) {
  return (
    <div
      className="card"
      style={{ padding: 'var(--space-5)', display: 'grid', gap: 'var(--space-2)' }}
    >
      <p className="field-help">
        Confirm taps, dry-runs, log-only results, and stale execution runs are captured when the
        Ticket 2 ledger is present on the tenant.
      </p>
      <p className="field-help">pg-boss final failures disappear after seven days.</p>
      {data?.freshness === 'partial' && (
        <output className="field-error" aria-live="polite">
          Partial results: {data.unavailableTenants.map((t) => t.tenant).join(', ')}
        </output>
      )}
      {data?.freshness === 'stale' && (
        <output className="field-error" aria-live="polite">
          Showing stale results from {data.staleAgeSeconds ?? 0}s ago.
        </output>
      )}
    </div>
  );
}

function ExecutionTable({
  data,
  loading,
  status,
  onSelect,
}: {
  data: ExecutionListPage | null;
  loading: boolean;
  status: ExecutionStatusFilter;
  onSelect: (item: ExecutionListItem) => void;
}) {
  if (loading && !data) return <p className="field-help">loading...</p>;
  if (!data?.items.length) {
    return (
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        <p className="field-help">
          {status === 'uncertain'
            ? 'No uncertain executions match these filters.'
            : 'no executions match these filters.'}
        </p>
      </div>
    );
  }
  return (
    <div className="card" style={{ overflow: 'auto', maxHeight: 'min(62vh, 720px)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1180 }}>
        <thead>
          <tr>
            {[
              'status',
              'class',
              'tenant / env',
              'source',
              'trigger / id',
              'failed step',
              'error preview',
              'occurred',
              'time',
              'detail',
            ].map((heading) => (
              <th key={heading} style={thStyle}>
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.items.map((item) => (
            <tr key={item.id}>
              <td style={tdStyle}>{item.status}</td>
              <td style={tdStyle}>{item.classification ?? '-'}</td>
              <td style={tdStyle}>
                <strong style={{ color: 'var(--ink)' }}>{item.tenant}</strong>
                <br />
                <span className="field-help">{item.environment}</span>
              </td>
              <td style={tdStyle}>{item.source}</td>
              <td style={tdStyle}>
                {item.triggerSummary}
                <br />
                <span className="field-help">{item.sourceId}</span>
              </td>
              <td style={tdStyle}>{item.failedStep ?? '-'}</td>
              <td style={tdStyle}>{item.errorPreview ?? '-'}</td>
              <td style={tdStyle}>{formatTime(item.occurredAt)}</td>
              <td style={tdStyle}>{timeBasisLabel(item.timeBasis)}</td>
              <td style={tdStyle}>
                <button
                  type="button"
                  onClick={() => onSelect(item)}
                  disabled={!item.detailAvailable}
                  className="btn-secondary"
                  style={{
                    width: '100%',
                    maxWidth: 150,
                    textAlign: 'left',
                    padding: '6px 10px',
                    whiteSpace: 'normal',
                  }}
                >
                  {item.detailAvailable ? 'open detail' : 'unavailable'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left' as const,
  borderBottom: '1px solid var(--rule)',
  fontSize: 'var(--fs-small)',
  color: 'var(--muted)',
  position: 'sticky' as const,
  top: 0,
  background: 'var(--paper)',
  zIndex: 1,
};

const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--rule)',
  verticalAlign: 'top' as const,
  fontSize: 'var(--fs-small)',
  color: 'var(--ink)',
};

function DetailDrawer({
  item,
  detail,
  error,
  onClose,
}: {
  item: ExecutionListItem;
  detail: ExecutionDetail | null;
  error: string | null;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.showModal === 'function' && !dialog.open) {
      dialog.showModal();
    } else if (!dialog.open) {
      dialog.setAttribute('open', '');
    }
    return () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      aria-modal="true"
      aria-label="execution detail"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,20,20,0.28)',
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        height: '100%',
        border: 0,
        padding: 0,
        maxWidth: 'none',
        maxHeight: 'none',
      }}
    >
      <div
        style={{
          width: 'min(640px, 100vw)',
          height: '100%',
          background: 'var(--paper)',
          borderLeft: '1px solid var(--rule)',
          padding: 'var(--space-6)',
          overflow: 'auto',
          display: 'grid',
          alignContent: 'start',
          gap: 'var(--space-4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
          <div>
            <h2 className="h3" style={{ color: 'var(--ink)' }}>
              {item.failedStep ?? item.source}
            </h2>
            <p className="field-help">
              {item.tenant} / {item.source}
            </p>
          </div>
          <button type="button" className="btn-secondary" onClick={onClose}>
            close
          </button>
        </div>
        {error ? (
          <p className="field-error">{error}</p>
        ) : detail ? (
          <>
            <p className="field-help">Authenticated detail. Credentials suppressed.</p>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                background: 'var(--ink)',
                color: 'var(--paper)',
                borderRadius: 6,
                padding: 'var(--space-4)',
                fontSize: 'var(--fs-mono-sm)',
              }}
            >
              {JSON.stringify(detail.detail, null, 2)}
            </pre>
          </>
        ) : (
          <p className="field-help">loading detail...</p>
        )}
      </div>
    </dialog>
  );
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function timeBasisLabel(value: ExecutionListItem['timeBasis']): string {
  return value === 'provisioning_start_fallback' ? 'provisioning start fallback' : 'exact';
}
